/*
 * Viretta v2 search (round 80, canvas page 11 Sog-desktop / Sog-mobil / Sog-regler). Loaded with defer by sections/header.liquid.
 *
 * · desktop (≥ 1150 px): focus in the header field opens the panel under the header (page dimmed, header not)
 * · smaller screens: the search icon opens the panel full screen; its own field gets the focus
 * · empty field = idle panel (popular searches, categories, help); from 2 characters the results are fetched from
 *   Shopify predictive search rendered by sections/v2-search-results (+ the total from sections/v2-search-count)
 * · Esc: first press clears the field, the second closes; ↓ / ↑ move through the links; Enter = the search page
 * · the last search is kept when the panel is closed and opened again
 * The panel is moved to <body> so position:fixed is never trapped inside the (sticky, animated) header section.
 */
(() => {
  const DESKTOP = window.matchMedia('(min-width: 1150px)');
  const FIELDS = 'title,product_type,variants.title';
  let panel, idle, results, msg, controller, timer, lastQ = '', openState = false;

  const headerSection = () => document.querySelector('.shopify-section--header');
  const storeHeader = () => document.querySelector('store-header');
  const headerInput = () => document.querySelector('.v2s-field--header [data-v2s-input]');
  const panelInput = () => panel?.querySelector('.v2s-field--panel [data-v2s-input]');
  // the visible field: header field (≥ 1380 px), else the field inside the panel (icon mode, phones)
  const activeInput = () => [headerInput(), panelInput()].find((i) => i && i.offsetParent !== null) || panelInput();
  const inputs = () => [headerInput(), panelInput()].filter(Boolean);

  const setTop = () => {
    if (!panel) return;
    const top = DESKTOP.matches ? Math.max(0, Math.round(headerSection()?.getBoundingClientRect().bottom || 0)) : 0;
    panel.style.setProperty('--v2s-top', `${top}px`);
  };

  const syncClear = () => inputs().forEach((input) => {
    const clear = input.parentElement.querySelector('[data-v2s-clear]');
    if (clear) clear.hidden = input.value.length === 0;
  });

  const setValue = (value) => { inputs().forEach((input) => { input.value = value; }); syncClear(); };

  const showIdle = (message) => {
    results.hidden = true;
    results.innerHTML = '';
    idle.hidden = false;
    msg.hidden = !message;
    msg.innerHTML = message || '';
  };

  const escapeHtml = (s) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  const fetchResults = async (q) => {
    controller?.abort();
    controller = new AbortController();
    const { signal } = controller;
    const suggest = `${panel.dataset.suggestUrl}?q=${encodeURIComponent(q)}&resources[type]=product,collection,article,query`
      + `&resources[limit]=6&resources[limit_scope]=each&resources[options][unavailable_products]=last`
      + `&resources[options][fields]=${FIELDS}&section_id=v2-search-results`;
    const count = `${panel.dataset.searchUrl}?q=${encodeURIComponent(q)}&type=product&options[prefix]=last`
      + `&options[fields]=${FIELDS}&section_id=v2-search-count`;
    panel.classList.add('is-loading');
    try {
      const [html, total] = await Promise.all([
        fetch(suggest, { signal }).then((r) => r.text()),
        fetch(count, { signal }).then((r) => r.text()).catch(() => '')
      ]);
      if (signal.aborted) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      if (doc.querySelector('[data-v2s-none]')) {
        showIdle(`Keine Ergebnisse für <b>»${escapeHtml(q)}«</b>. Prüf die Schreibweise – oder sieh dir an, wonach andere suchen.`);
        return;
      }
      const res = doc.querySelector('.v2s-res');
      if (!res) { showIdle(); return; }
      const n = parseInt(new DOMParser().parseFromString(total, 'text/html').querySelector('[data-v2s-total]')?.dataset.v2sTotal || '0', 10);
      const all = res.querySelector('[data-v2s-all]');
      const countEl = res.querySelector('[data-v2s-count]');
      if (n > 0) {
        if (all) all.textContent = n === 1 ? 'Ergebnis anzeigen' : `Alle ${n} Ergebnisse anzeigen`;
        if (countEl) countEl.textContent = `${n} ${n === 1 ? 'Ergebnis' : 'Ergebnisse'} für »${q}«`;
      }
      results.replaceChildren(res);
      results.hidden = false;
      idle.hidden = true;
      msg.hidden = true;
    } catch (error) {
      if (error.name !== 'AbortError') showIdle();
    } finally {
      if (!signal.aborted) panel.classList.remove('is-loading');
    }
  };

  const onInput = (value) => {
    syncClear();
    clearTimeout(timer);
    const q = value.trim();
    lastQ = q;
    if (q.length < 2) { controller?.abort(); panel.classList.remove('is-loading'); showIdle(); return; }
    timer = setTimeout(() => fetchResults(q), 180);
  };

  const open = (focusPanelField) => {
    if (!panel) return;
    panel.classList.toggle('v2s--own', !!focusPanelField && !DESKTOP.matches);   // desktop: the field opens in the header bar
    setTop();
    if (!openState) {
      openState = true;
      panel.hidden = false;
      document.documentElement.classList.add('v2s-is-open');
      storeHeader()?.classList.add('is-filled');
      requestAnimationFrame(() => panel.classList.add('is-open'));
      headerInput()?.setAttribute('aria-expanded', 'true');
    }
    if (focusPanelField || !DESKTOP.matches) {
      const input = DESKTOP.matches ? headerInput() : panelInput();
      setTimeout(() => input?.focus({ preventScroll: true }), 30);
    }
  };

  const close = () => {
    if (!panel || !openState) return;
    openState = false;
    panel.classList.remove('is-open');
    panel.hidden = true;
    document.documentElement.classList.remove('v2s-is-open');
    storeHeader()?.classList.toggle('is-filled', window.scrollY > 50);
    headerInput()?.setAttribute('aria-expanded', 'false');
    if (document.activeElement && panel.contains(document.activeElement)) document.activeElement.blur();
  };

  const submitActive = () => {
    const form = activeInput()?.form;
    if (!form) return;
    if (!activeInput().value.trim()) { activeInput().focus(); return; }
    form.requestSubmit ? form.requestSubmit() : form.submit();
  };

  const init = () => {
    const fresh = document.querySelector('.shopify-section--header #v2s-panel, store-header ~ #v2s-panel, height-observer ~ #v2s-panel');
    const candidate = fresh || document.getElementById('v2s-panel');
    if (!candidate) return;
    // theme editor re-render: drop the old moved panel, move the new one
    document.querySelectorAll('body > #v2s-panel').forEach((old) => { if (old !== candidate) old.remove(); });
    if (candidate.parentElement !== document.body) document.body.appendChild(candidate);
    panel = candidate;
    idle = panel.querySelector('[data-v2s-idle]');
    results = panel.querySelector('[data-v2s-results]');
    msg = panel.querySelector('[data-v2s-msg]');
    openState = !panel.hidden;
    if (lastQ) setValue(lastQ);
  };

  /* events – delegated, so they survive header re-renders ------------------------------------------------------ */

  document.addEventListener('focusin', (event) => {
    if (event.target.matches?.('.v2s-field--header [data-v2s-input]')) open(false);
  });

  document.addEventListener('input', (event) => {
    if (!event.target.matches?.('[data-v2s-input]')) return;
    const value = event.target.value;
    inputs().forEach((input) => { if (input !== event.target) input.value = value; });
    if (!openState) open(false);
    onInput(value);
  });

  document.addEventListener('click', (event) => {
    const t = event.target;
    if (t.closest?.('[data-v2s-open]')) {
      event.preventDefault();
      if (openState && DESKTOP.matches) { close(); return; }   // the × in the header closes
      document.getElementById('header-sidebar-menu')?.hide?.();
      open(true);
      return;
    }
    if (!panel) return;
    if (t.closest?.('[data-v2s-clear]')) {
      event.preventDefault();
      setValue('');
      onInput('');
      activeInput()?.focus();
      return;
    }
    if (t.closest?.('[data-v2s-close]')) { event.preventDefault(); close(); return; }
    if (t.closest?.('[data-v2s-all]')) { event.preventDefault(); submitActive(); return; }
    const chip = t.closest?.('[data-v2s-q]');
    if (chip && panel.contains(chip)) {
      event.preventDefault();
      setValue(chip.dataset.v2sQ);
      onInput(chip.dataset.v2sQ);
      activeInput()?.focus();
      return;
    }
    // desktop: a click outside the header field and the panel closes it
    if (openState && DESKTOP.matches && !panel.querySelector('.v2s__sheet').contains(t) && !t.closest?.('.v2s-field--header')) close();
  });

  document.addEventListener('keydown', (event) => {
    if (!openState || !panel) return;
    const inField = event.target.matches?.('[data-v2s-input]');
    if (event.key === 'Escape') {
      event.preventDefault();
      const input = inField ? event.target : activeInput();
      if (input && input.value) { setValue(''); onInput(''); input.focus(); } else { close(); headerInput()?.blur(); }
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      const items = Array.from(panel.querySelectorAll('[data-v2s-item]')).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const i = items.indexOf(document.activeElement);
      if (!inField && i === -1) return;
      event.preventDefault();
      if (event.key === 'ArrowDown') items[Math.min(items.length - 1, i + 1)].focus();
      else if (i <= 0) activeInput()?.focus();
      else items[i - 1].focus();
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest?.('[data-v2s-form]');
    if (form && !form.querySelector('[data-v2s-input]').value.trim()) event.preventDefault();
  });

  window.addEventListener('resize', () => { if (openState) setTop(); }, { passive: true });
  DESKTOP.addEventListener?.('change', close);
  document.addEventListener('shopify:section:load', init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
